/*
  Product page live frame preview — The Poster Club architecture.
  TPC stacks layers over the flat print image (.print-preview__layer--frame,
  one generic frame asset per colour). We do the same: the frameless print
  mockup stays as the base image and the selected frame colour is drawn on
  top as an overlay, so no per-product framed mockups are ever needed.
*/
(() => {
  'use strict';

  const TR_MAP = { ç: 'c', ğ: 'g', ı: 'i', İ: 'i', ö: 'o', ş: 's', ü: 'u' };
  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/[çğıİöşü]/g, (ch) => TR_MAP[ch] || ch)
      .replace(/\s+/g, ' ')
      .trim();

  const isFrameless = (v) => {
    const n = norm(v);
    if (!n) return true;
    if (
      n.includes('cercevesiz') ||
      n.includes('no frame') ||
      n.includes('frameless') ||
      n.includes('unframed')
    ) {
      return true;
    }
    return (
      n === 'yok' ||
      n === 'none' ||
      n === 'n/a' ||
      n === 'na' ||
      n === '-' ||
      n === '—' ||
      n === 'gecersiz' ||
      n === 'gecerli degil'
    );
  };

  const isSiyahValue = (v) => {
    const n = norm(v);
    if (!n || isFrameless(v)) return false;
    return n === 'siyah' || n === 'black' || n.startsWith('siyah ') || n.startsWith('black ');
  };

  const isFramedTypeValue = (v) => {
    const n = norm(v);
    return n === 'cerceveli' || n === 'framed' || n === 'with frame' || n.startsWith('cerceveli');
  };

  const FRAME_TYPE_HANDLES = ['cerceve', 'frame', 'cerceve-secenegi', 'cerceve-secimi'];
  const FRAME_COLOR_ONLY_HANDLES = [
    'cerceve-rengi',
    'cerceve-renk',
    'cerceve-reng',
    'frame-color',
    'frame-colour',
  ];

  const scriptRoot = (script) =>
    (script && script.closest('quick-add-modal, .quick-add-modal')) ||
    (script && script.closest('[id^="shopify-section-"]')) ||
    (script && script.closest('.product')) ||
    null;

  const galleryForConfig = (config) => {
    const sel = `[data-section="product-media-${config.sectionId}"]`;
    if (config.root && config.root.querySelector) {
      const scoped = config.root.querySelector(sel);
      if (scoped) return scoped;
    }
    const modals = document.querySelectorAll('quick-add-modal, .quick-add-modal');
    for (const modal of modals) {
      const g = modal.querySelector(sel);
      if (g) return g;
    }
    return document.querySelector(sel);
  };

  /*
    Paspas kuralı paspas-kurali.js'te tek yerde duruyor; duvar tasarımcısı da
    aynı işlevi çağırıyor, böylece iki yüzey birbirinden ayrışamaz.
  */
  const matEserKutusu = (kagitG, kagitY) => window.AlaPaspas.eserKutusu(kagitG, kagitY);

  /*
    Baskı yerleşimi bir varyant değil, tarayıcı tarafında bir çizim kipi.
    Öntanımlı Tam Oturt: müşteri Ortala'yı kendisi seçmedikçe önizleme
    bugünküyle birebir aynı kalır. Duvar tasarımcısındaki liste ile aynı.
  */
  const PLACEMENT_MODES = ['Tam Oturt', 'Ortala'];

  /*
    Frame palette — TPC carries ~20 frame colours; every entry here works the
    moment a matching option value ("Çerçeve") exists on any product.
    Keys are normalized (Turkish chars folded). English aliases included.
  */
  const FRAME_COLORS = {
    siyah: '#141416',
    black: '#141416',
    'siyah ahsap': '#141416',
    beyaz: '#f5f3ef',
    white: '#f5f3ef',
    kahverengi: '#5a3a26',
    brown: '#5a3a26',
    mese: '#c69f68',
    oak: '#c69f68',
    'koyu mese': '#8a5c34',
    'dark oak': '#8a5c34',
    ceviz: '#6b4a2f',
    walnut: '#6b4a2f',
    'dogal ahsap': '#d3b285',
    natural: '#d3b285',
    yesil: '#37503f',
    green: '#37503f',
    'acik mavi': '#a9c4d8',
    'light blue': '#a9c4d8',
    mavi: '#33517c',
    blue: '#33517c',
    lacivert: '#232f4b',
    navy: '#232f4b',
    kirmizi: '#a63127',
    red: '#a63127',
    bordo: '#6e2231',
    burgundy: '#6e2231',
    turuncu: '#d4712f',
    orange: '#d4712f',
    sari: '#ddb23e',
    yellow: '#ddb23e',
    pembe: '#e2b5c0',
    pink: '#e2b5c0',
    murdum: '#57324a',
    plum: '#57324a',
    lavanta: '#b2a3cf',
    lavender: '#b2a3cf',
    nane: '#a6c9b4',
    mint: '#a6c9b4',
    bej: '#d8c8a6',
    beige: '#d8c8a6',
    petrol: '#2a5058',
    gri: '#9a9aa0',
    grey: '#9a9aa0',
    gray: '#9a9aa0',
    'siyah aluminyum': '#2b2b2f',
    'black alu': '#2b2b2f',
    gumus: '#c3c3c8',
    silver: '#c3c3c8',
    pirinc: '#b28c3f',
    brass: '#b28c3f',
    altin: '#c19a4b',
    gold: '#c19a4b',
  };

  const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  };

  // Lighten (f > 1) / darken (f < 1) — used to bevel the four moulding sides
  // so the CSS border miters read like a real frame, matching TPC's PNGs.
  const shade = (hex, f) => {
    const [r, g, b] = hexToRgb(hex).map((c) =>
      Math.max(0, Math.min(255, Math.round(c * f + (f > 1 ? (f - 1) * 40 : 0))))
    );
    return `rgb(${r}, ${g}, ${b})`;
  };

  const frameColor = (value) => {
    const key = norm(value);
    if (FRAME_COLORS[key]) return FRAME_COLORS[key];
    // Partial match: "Siyah Çerçeve", "Meşe Ahşap" vb.
    const found = Object.keys(FRAME_COLORS).find(
      (k) => key.includes(k) || k.includes(key)
    );
    return found ? FRAME_COLORS[found] : '#1a1a1a';
  };

  class ProductFramePreview {
    cropPosition() {
      return this.cfg && this.cfg.crop === 'top' ? 'top' : 'center';
    }

    constructor(config) {
      this.cfg = config;
      this.gallery = galleryForConfig(config);
      if (!this.gallery) return;

      this.mediaByAlt = {};
      (config.media || []).forEach((m) => {
        if (m.alt) this.mediaByAlt[norm(m.alt)] = m.id;
      });
      this.firstImageId = (config.media || []).find((m) => m.type === 'image')?.id || null;

      // Baked framed mockups ("Siyah|Tam Oturt" etc.) are obsolete — the frame
      // is always drawn live over the frameless print, TPC style. Drop them
      // from every gallery so they never appear.
      this.bakedIds = (config.media || [])
        .filter((m) => {
          const a = norm(m.alt);
          return a.includes('|') && !a.startsWith('cercevesiz|');
        })
        .map((m) => m.id);

      this.options = (config.initialOptions || []).slice();
      if (this.cfg.frameIndex > -1 && isFrameless(this.options[this.cfg.frameIndex])) {
        this.options[this.cfg.frameIndex] = 'Siyah';
      }
      this.raf = null;
      this._swiperBound = false;
      this.fitLock = norm(config.fitLock) === 'contain' ? 'contain' : '';
      this.placementMode = this.fitLock === 'contain' ? 'Ortala' : PLACEMENT_MODES[0];
      this.gallery._pfp = this;

      /*
        CLS koruması: swiper kurulumu + baked slaytların sökülmesi +
        çerçeve çizimi sırasında galeri bir an 0 yüksekliğe düşüyor,
        ürün bilgisi yukarı sıçrayıp geri iniyordu (mobil CLS ~0.45).
        İlk boyamadaki yükseklik kilitlenir; çerçeve oturduktan sonra
        (update tamamlanınca) veya en geç 5 sn'de serbest bırakılır.
        Kilit yalnızca min-height olduğundan büyüme engellenmez ve
        nihai görünüm bugünkünün aynısıdır.
      */
      this._heightLocked = false;
      this.lockGalleryHeight();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event?.data || {};
          if (String(data.sectionId) !== String(config.sectionId)) return;
          if (data.variant?.options) {
            this.options = data.variant.options;
            this.scheduleUpdate();
            /* variantChange sonrası DOM bazen geç oturur; tek rAF yetmez */
            setTimeout(() => this.scheduleUpdate(), 350);
          }
        });
      }

      this.bindPlacementPicker();
      this.bindSwiperSync();

      window.addEventListener('resize', () => this.scheduleUpdate());

      // Gallery subtree can be replaced after fetch (variant media modes).
      new MutationObserver(() => this.scheduleUpdate()).observe(this.gallery, {
        childList: true,
        subtree: true,
      });

      this.update();
    }

    /*
      Swiper kaydırırken getBoundingClientRect ara karede yanlış olur;
      çerçeve bir an eserin altında kalabiliyor. Animasyon bitince yeniden
      ölç, maliyet: yalnızca olay anında bir scheduleUpdate.
    */
    bindSwiperSync() {
      const attach = () => {
        if (this._swiperBound) return;
        const slider = this.gallery.querySelector('.js-media-list');
        const sw = slider?.swiper;
        if (!sw) return;
        this._swiperBound = true;
        sw.on('slideChangeTransitionEnd', () => this.scheduleUpdate());
        sw.on('transitionEnd', () => this.scheduleUpdate());
      };
      attach();
      setTimeout(attach, 120);
      setTimeout(attach, 600);
    }

    swiperBusy() {
      const sw = this.gallery.querySelector('.js-media-list')?.swiper;
      return !!(sw && (sw.animating || sw.touchEventsData?.isTouched));
    }

    /*
      Tek rAF yetmeyebiliyor: görsel boyutu ve swiper transform aynı karede
      oturmuyor. İki rAF (~32 ms) ağ isteği eklemez, yalnızca layout bekler.
    */
    scheduleUpdate() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          if (this.swiperBusy()) {
            this.scheduleUpdate();
            return;
          }
          this.update();
        });
      });
    }

    selectedFrame() {
      return this.cfg.frameIndex > -1 ? this.options[this.cfg.frameIndex] : null;
    }

    syncFrameFromDom(frameValue) {
      if (this.cfg.frameIndex > -1 && frameValue) {
        const opts = Array.isArray(this.options) ? this.options.slice() : [];
        opts[this.cfg.frameIndex] = frameValue;
        this.options = opts;
      }
      this.scheduleUpdate();
    }

    /*
      Seçili baskı oranı. Boyut her zaman biliniyor: Shopify sayfayı
      selected_or_first_available_variant ile basar, yani ilk açılışta bile
      bir varyant, dolayısıyla bir boyut seçilidir; varyant değiştikçe
      options güncelleniyor. Yine de değer "50x70" kalıbına uymazsa null
      döner ve çizim eserin kendi oranına düşer.
    */
    baskiOrani() {
      if (this.cfg.sizeIndex < 0) return null;
      return window.AlaPaspas.baskiOrani(this.options[this.cfg.sizeIndex]);
    }

    /*
      Yerleşim önce varyant seçeneğinden okunur (bir ürün ileride gerçekten
      bu seçeneği taşırsa çalışsın diye), yoksa müşterinin seçtiği çizim kipi
      kullanılır. Hiçbiri yoksa Tam Oturt.
    */
    selectedPlacement() {
      if (this.fitLock === 'contain') return 'Ortala';
      if (this.cfg.placementIndex > -1) return this.options[this.cfg.placementIndex];
      return this.placementMode || PLACEMENT_MODES[0];
    }

    /* Yerleşim seçicisi: sabit kip listesi, varyanta ve fiyata dokunmaz */
    bindPlacementPicker() {
      const picker = document.querySelector(
        `[data-pfp-placement-picker="${this.cfg.sectionId}"]`
      );
      if (!picker) return;
      if (this.fitLock !== 'contain') this.placementMode = PLACEMENT_MODES[0];
      picker.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-pfp-placement]');
        if (!btn) return;
        const kip = btn.dataset.pfpPlacement;
        if (this.fitLock === 'contain' && !norm(kip).includes('ortala')) return;
        if (!PLACEMENT_MODES.includes(kip) || kip === this.placementMode) return;
        this.placementMode = this.fitLock === 'contain' ? 'Ortala' : kip;
        picker.querySelectorAll('[data-pfp-placement]').forEach((b) => {
          const aktif = b.dataset.pfpPlacement === kip;
          b.classList.toggle('is-active', aktif);
          b.setAttribute('aria-pressed', aktif ? 'true' : 'false');
        });
        this.scheduleUpdate();
      });
    }

    findMediaId(frame, placement) {
      const key = norm(`${frame}|${placement}`);
      return this.mediaByAlt[key] || null;
    }

    /*
      Resolves the base art image for the selected placement.
      If "Ortala" is selected but no Ortala mockup was uploaded, we fall back
      to the Tam Oturt (full-bleed) image and synthesize the centered look
      live: art scaled to MAT_SCALE on white paper (the exact Photoshop rule —
      guides at 12% from each side, vertically centered).
    */
    resolveArt(placement) {
      const direct =
        this.findMediaId('Çerçevesiz', placement) ||
        this.findMediaId('Cercevesiz', placement);
      if (direct) return { id: direct, synthMat: false };

      const wantsMat = norm(placement).includes('ortala');
      const fallback =
        this.findMediaId('Çerçevesiz', 'Tam Oturt') ||
        Object.keys(this.mediaByAlt)
          .filter((k) => k.startsWith('cercevesiz|'))
          .map((k) => this.mediaByAlt[k])[0] ||
        this.firstImageId;

      return { id: fallback, synthMat: wantsMat && !!fallback };
    }

    slidesFor(mediaId) {
      if (!mediaId) return [];
      return [
        ...this.gallery.querySelectorAll(
          `[data-media-id="${this.cfg.sectionId}-${mediaId}"], [data-media-sub-id="${this.cfg.sectionId}-${mediaId}"]`
        ),
      ];
    }

    hideAll() {
      this.gallery
        .querySelectorAll('.pfp-frame, .pfp-paper')
        .forEach((el) => el.classList.remove('is-visible'));
      /* Kağıt için gizlenen galeri görselleri geri açılır */
      this.gallery.querySelectorAll('img.pfp-gizli').forEach((img) => {
        img.style.visibility = '';
        img.classList.remove('pfp-gizli');
      });
    }

    removeBakedSlides() {
      if (!this.bakedIds.length) return;
      let removed = false;
      this.bakedIds.forEach((id) => {
        const selectors = [
          // main gallery + thumbnail sublist + stacked/grid containers
          `[data-media-id="${this.cfg.sectionId}-${id}"]`,
          `[data-media-sub-id="${this.cfg.sectionId}-${id}"]`,
          // zoom modal slides use the bare media id
          `#ProductModal-${this.cfg.sectionId} [data-media-id="${id}"]`,
        ];
        document
          .querySelectorAll(selectors.join(', '))
          .forEach((el) => {
            el.remove();
            removed = true;
          });
      });
      if (!removed) return;
      ['.js-media-list', '.js-media-sublist'].forEach((sel) => {
        const el = this.gallery.querySelector(sel);
        if (el && el.swiper) el.swiper.update();
      });
    }

    markReadyIfSettled() {
      if (this.gallery.dataset.pfpDefaultSettled === '1') this.gallery.classList.add('pfp-ready');
    }

    lockGalleryHeight() {
      if (this._heightLocked) return;
      const h = this.gallery.offsetHeight;
      if (h > 80) {
        this._heightLocked = true;
        this.gallery.style.minHeight = h + 'px';
        this._lockTimer = setTimeout(() => this.releaseGalleryHeight(), 5000);
      }
    }

    releaseGalleryHeight() {
      if (!this._heightLocked) return;
      this._heightLocked = false;
      if (this._lockTimer) clearTimeout(this._lockTimer);
      /* Yeni yerleşim otursun diye iki kare bekle, sonra kilidi bırak. */
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.gallery.style.minHeight = '';
        })
      );
    }

    update() {
      if (!this.gallery.isConnected) {
        this.gallery = galleryForConfig(this.cfg) || this.gallery;
      }

      this.removeBakedSlides();

      const frame = this.selectedFrame();
      const placement = this.selectedPlacement();
      const framed = frame && !isFrameless(frame);

      this.hideAll();

      const art = this.resolveArt(placement);
      if (!art.id) {
        this.markReadyIfSettled();
        if (this.gallery.dataset.pfpDefaultSettled === '1') this.releaseGalleryHeight();
        return;
      }
      /*
        Çerçevesiz Tam Oturt'ta bile çizim var: kağıt baskı oranında
        olduğundan eser kırpılarak gösterilmeli. Baskı oranı çözülemiyorsa
        çizecek bir şey yok, eski davranışa dönülür.
      */
      if (!framed && !art.synthMat && !this.baskiOrani()) {
        this.markReadyIfSettled();
        if (this.gallery.dataset.pfpDefaultSettled === '1') this.releaseGalleryHeight();
        return;
      }

      const slides = this.slidesFor(art.id);
      slides.forEach((slide) =>
        this.drawLayers(slide, framed ? frame : null, art.synthMat)
      );
      this.slideTo(art.id);
      if (this.gallery.querySelector('.pfp-paper.is-visible, .pfp-frame.is-visible')) {
        this.markReadyIfSettled();
      }

      /* Yakınlaştırma penceresi ve kırpma notu yalnızca Tam Oturt'ta */
      const oran = this.baskiOrani();
      this.kirpZoom(art.id, oran, !art.synthMat);
      this.kirpmaNotu(oran, !art.synthMat);
      document.dispatchEvent(
        new CustomEvent('pfp:updated', { detail: { sectionId: this.cfg.sectionId } })
      );
      /*
        Varsayılan çerçeve seçimi oturduktan sonraki ilk tam çizimde
        yükseklik kilidi bırakılır — nihai yerleşim artık belli.
      */
      if (this.gallery.dataset.pfpDefaultSettled === '1') this.releaseGalleryHeight();
    }

    /*
      Kırpma notu: yalnızca kayıp eşiği aştığında yazılır. Metin her zaman
      aynı yerde durur ve kutunun yüksekliği CSS'te sabitlendiği için
      müşteri boyutlar arasında gezerken sayfa oynamaz.
    */
    kirpmaNotu(oran, tamOturt) {
      const kutu = document.querySelector(`[data-pfp-crop-note="${this.cfg.sectionId}"]`);
      if (!kutu) return;
      const metin = kutu.querySelector('.product-crop-note__text') || kutu;
      const img = this.gallery.querySelector('.product__media img');
      const eserOran = img?.naturalWidth && img?.naturalHeight
        ? img.naturalWidth / img.naturalHeight
        : null;

      if (!tamOturt || !oran || !eserOran || !window.AlaPaspas.kirpmaUyarisi(eserOran, oran)) {
        metin.textContent = '';
        return;
      }
      const eksen = eserOran > oran ? 'yanlardan' : 'alt ve üstten';
      metin.textContent = `Bu ölçüde eserin ${eksen} bir bölümü kesilir. Eserin tamamını görmek için ortala yerleşimini seçebilirsiniz.`;
    }

    slideTo(mediaId) {
      const sliderEl = this.gallery.querySelector('.js-media-list');
      if (!sliderEl || !sliderEl.swiper) return;
      const target = `${this.cfg.sectionId}-${mediaId}`;
      const index = sliderEl.swiper.slides.findIndex(
        (s) => s.dataset?.mediaId === target
      );
      if (index > -1 && sliderEl.swiper.activeIndex !== index) {
        sliderEl.swiper.slideTo(index, 500);
      }
    }

    layerEl(slide, className) {
      let el = slide.querySelector(`:scope > .${className}`);
      if (!el) {
        el = document.createElement('div');
        el.className = className;
        el.setAttribute('aria-hidden', 'true');
        slide.appendChild(el);
      }
      return el;
    }

    // Painted rectangle of the art, object-fit aware and transform-free
    // (TPC: layers align to the contained print, not the media box).
    paintedRect(slide, img) {
      const prevTransform = img.style.transform;
      img.style.transform = 'none';
      const sRect = slide.getBoundingClientRect();
      const iRect = img.getBoundingClientRect();
      img.style.transform = prevTransform;
      if (!sRect.width || !iRect.width) return null;

      let x = iRect.left - sRect.left;
      let y = iRect.top - sRect.top;
      let w = iRect.width;
      let h = iRect.height;

      const fit = getComputedStyle(img).objectFit;
      if (fit === 'contain' || fit === 'scale-down') {
        const scale = Math.min(w / img.naturalWidth, h / img.naturalHeight);
        const pw = img.naturalWidth * scale;
        const ph = img.naturalHeight * scale;
        x += (w - pw) / 2;
        y += (h - ph) / 2;
        w = pw;
        h = ph;
      }
      return { x, y, w, h };
    }

    /*
      Eserin sığdığı alan: görsel elemanının kutusu, slayta göre. Kağıt bu
      alanın içine, baskı oranında ve ortalanmış olarak oturur.
    */
    mediaKutusu(slide, img) {
      const s = slide.getBoundingClientRect();
      const i = img.getBoundingClientRect();
      return { x: i.left - s.left, y: i.top - s.top, w: i.width, h: i.height };
    }

    /*
      Kağıt baskı oranındayken alttaki galeri görseli kağıdın dışına taşar,
      bu yüzden gizlenir. Yer kaplamaya devam etsin diye display değil
      visibility kullanılır: ölçüler bozulmaz. Çağıran taraf kopyanın
      yüklendiğini zaten doğruluyor.
    */
    gizleAsil(img) {
      img.style.visibility = 'hidden';
      img.classList.add('pfp-gizli');
    }

    /*
      Yakınlaştırma penceresi: basılmayacak alanı orada da göstermeyiz.

      Pencere slaytları kendi medya kimliğini çıplak taşıyor (galeri
      "bolum-kimlik" kullanıyor), bu yüzden ayrı bir seçiciyle bulunuyorlar.
      Paylaşılan medya işaretlemesine dokunmaya gerek yok: kırpma yalnızca
      görselin kendi stiline yazılan clip-path ile yapılıyor.

      clip-path dönüşümlerden önce, elemanın kendi koordinatlarında
      uygulanır; swiper'ın yakınlaştırma dönüşümü sonra geldiği için müşteri
      yakınlaştırdıkça kırpılmış alan da birlikte büyür, yani basılan
      bölgenin içine yakınlaşılır.

      Yalnızca Tam Oturt'ta kırpılır. Ortala'da eserin tamamı basıldığı için
      pencerede de tamamı görünmeli.
    */
    kirpZoom(mediaId, oran, kirp) {
      const modal = document.getElementById(`ProductModal-${this.cfg.sectionId}`);
      if (!modal) return;
      const slayt = modal.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slayt) return;
      slayt.querySelectorAll('img').forEach((im) => {
        if (!kirp || !oran) {
          im.style.clipPath = '';
          return;
        }
        const a = im.naturalWidth && im.naturalHeight
          ? im.naturalWidth / im.naturalHeight
          : null;
        if (!a) {
          /* Adres modal açılınca dolduruluyor, o zaman yeniden dene */
          im.addEventListener('load', () => this.scheduleUpdate(), { once: true });
          im.style.clipPath = '';
          return;
        }
        /* Kesme iki kenardan eşit: kirpmaHizasi orta */
        if (a > oran) {
          const p = ((1 - oran / a) / 2) * 100;
          im.style.clipPath = `inset(0 ${p.toFixed(2)}% 0 ${p.toFixed(2)}%)`;
        } else if (a < oran) {
          const p = ((1 - a / oran) / 2) * 100;
          im.style.clipPath = `inset(${p.toFixed(2)}% 0 ${p.toFixed(2)}% 0)`;
        } else {
          im.style.clipPath = '';
        }
      });
    }

    drawLayers(slide, frame, synthMat) {
      const img = slide.querySelector('.product__media img');
      if (!img) return;
      const cerceve = slide.querySelector(':scope > .pfp-frame');
      if (!img.complete || !img.naturalWidth) {
        img.addEventListener('load', () => this.scheduleUpdate(), { once: true });
        cerceve?.classList.remove('is-visible');
        return;
      }

      const boyanan = this.paintedRect(slide, img);
      if (!boyanan) return;

      /*
        Kağıt, eserin kendi oranına değil seçili baskı ölçüsüne göre çizilir:
        müşteri satın aldığı şeyi görsün. Eserin oranı kağıtla tutmadığında
        fark artık gizlenmiyor, Ortala'da mektup kutusu, Tam Oturt'ta kırpma
        olarak görünüyor. Duvar tasarımcısı zaten böyle çiziyordu.

        Kağıt, eserin sığdığı alanla aynı alana yerleştirilir: eserin medya
        kutusu. Böylece önizleme bugünkü kadar yer kaplar, yalnızca oranı
        baskıya uyar.

        Baskı oranı çözülemezse (boyut seçeneği yok ya da "50x70" kalıbına
        uymuyor) eski davranışa, eserin kendi boyanan dikdörtgenine dönülür.
      */
      const oran = this.baskiOrani();
      let kagit;
      if (oran) {
        const alan = this.mediaKutusu(slide, img);
        const k = window.AlaPaspas.kagitKutusu(alan.x, alan.y, alan.w, alan.h, oran);
        kagit = { x: k.x, y: k.y, w: k.g, h: k.yuk };
      } else {
        kagit = { x: boyanan.x, y: boyanan.y, w: boyanan.w, h: boyanan.h };
      }
      const { x, y, w, h } = kagit;

      /*
        Kağıt katmanı iki kipte de çizilir. Ortala'da paspas rengi üstünde
        eser paspas kutusuna sığar; Tam Oturt'ta eser kağıdı tamamen kaplar
        ve taşan yer kırpılır. Kağıt baskı oranındayken alttaki galeri
        görseli kağıdın dışına taşacağı için gizlenir; kopya yüklenmeden
        gizlenmez, yoksa bir an boş kalırdı.
      */
      if (oran || synthMat) {
        const paper = this.layerEl(slide, 'pfp-paper');
        paper.style.left = `${x.toFixed(1)}px`;
        paper.style.top = `${y.toFixed(1)}px`;
        paper.style.width = `${w.toFixed(1)}px`;
        paper.style.height = `${h.toFixed(1)}px`;
        paper.classList.toggle('is-cover', !synthMat);
        paper.classList.toggle('is-mat', !!synthMat);

        let art = paper.querySelector('img');
        if (!art) {
          art = document.createElement('img');
          art.alt = '';
          art.draggable = false;
          art.className = 'pfp-art product__media-image--protected';
          paper.appendChild(art);
        }

        const src = img.currentSrc || img.src;
        if (art.getAttribute('src') !== src) art.src = src;

        if (synthMat) {
          const kutu = matEserKutusu(w, h);
          art.style.setProperty('width', `${kutu.g.toFixed(1)}px`, 'important');
          art.style.setProperty('height', `${kutu.y.toFixed(1)}px`, 'important');
          art.style.removeProperty('object-fit');
          art.style.removeProperty('max-width');
          art.style.removeProperty('max-height');
          paper.style.background = window.AlaPaspas.kagitRenk || '#f4f1ea';
          paper.style.backgroundImage = '';
        } else {
          /*
            Tam Oturt: kağıdı kapla, taşanı kırp. Ölçek CSS'te object-fit:cover
            (Math.max ile aynı). Piksel width/height yazılmaz: masaüstü tema
            kuralı width:auto !important ile eziyordu ve çerçevede beyaz
            şerit kalıyordu. Kağıt zemini de aynı eserle örtülür; PNG
            saydamlığında beyaz kâğıt görünmesin.
          */
          const cropPos = this.cropPosition();
          art.style.setProperty('width', '100%', 'important');
          art.style.setProperty('height', '100%', 'important');
          art.style.setProperty('object-fit', 'cover', 'important');
          art.style.setProperty('object-position', cropPos);
          art.style.setProperty('max-width', 'none', 'important');
          art.style.setProperty('max-height', 'none', 'important');
          paper.dataset.crop = cropPos;
          paper.classList.toggle('cfp-crop-top', cropPos === 'top');
          paper.style.backgroundColor = 'transparent';
          paper.style.backgroundImage = `url("${String(src).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")`;
          paper.style.backgroundSize = 'cover';
          paper.style.backgroundPosition = cropPos;
          paper.style.backgroundRepeat = 'no-repeat';
        }

        /*
          Kopya yüklenmeden ne kağıt ne çerçeve gösterilir. Aksi halde
          çerçeve baskı oranında çizilirken içindeki eser hâlâ kendi
          oranındaki asıl görsel olur ve ikisi tutmadığı için çerçevenin
          içinde bir an beyaz şeritler görünür. Tam Oturt'ta beyaz şerit
          kabul edilmiyor, o yüzden çizim kopya hazır olana kadar bekler;
          bu arada ekranda değişiklik yapılmamış asıl görsel durur.

          Beklemenin pratikte bir bedeli yok: kopyanın adresi asıl görselin
          currentSrc'si, yani tarayıcı önbelleğinde hazır.
        */
        if (!art.complete || !art.naturalWidth) {
          art.addEventListener('load', () => this.scheduleUpdate(), { once: true });
          cerceve?.classList.remove('is-visible');
          return;
        }

        paper.classList.add('is-visible');
        if (oran) this.gizleAsil(img);
      }

      if (frame) {
        const overlay = cerceve || this.layerEl(slide, 'pfp-frame');
        /* Kağıt beklerken çerçeveyi gösterme: aynı kutuda ikisi birlikte gelmeli */
        const paper = slide.querySelector(':scope > .pfp-paper');
        const kagitHazir = !(oran || synthMat) || paper?.classList.contains('is-visible');
        if (!kagitHazir) {
          overlay.classList.remove('is-visible');
          return;
        }
        // Moulding ≈ TPC look: ~4% of the short side (1.5 cm on a 40 cm print).
        const lip = Math.max(6, Math.min(w, h) * 0.042);
        const base = frameColor(frame);
        overlay.style.left = `${x.toFixed(1)}px`;
        overlay.style.top = `${y.toFixed(1)}px`;
        overlay.style.width = `${w.toFixed(1)}px`;
        overlay.style.height = `${h.toFixed(1)}px`;
        overlay.style.borderStyle = 'solid';
        overlay.style.borderWidth = `${lip.toFixed(1)}px`;
        overlay.style.setProperty('--pfp-lip', `${lip.toFixed(1)}px`);
        overlay.style.borderTopColor = shade(base, 1.14);
        overlay.style.borderLeftColor = shade(base, 1.05);
        overlay.style.borderRightColor = shade(base, 0.93);
        overlay.style.borderBottomColor = shade(base, 0.8);
        overlay.classList.add('is-visible');
      }
    }
  }

  /*
    İlk açılış her zaman Siyah çerçeve: PDP ve her Hızlı bakış modalı
    ayrı uygulanır. Global one-shot yok — ikinci QV de Siyah açılır.
    Çerçevesiz / none / slash-swatch yalnızca müşteri elle seçerse kalır.
  */
  const findFrameFieldsets = (root) => {
    const all = [...root.querySelectorAll('fieldset[data-option-handle]')];
    const colorOnly = all.find((fs) =>
      FRAME_COLOR_ONLY_HANDLES.includes(fs.getAttribute('data-option-handle') || '')
    );
    const typeOrCombo = all.find((fs) =>
      FRAME_TYPE_HANDLES.includes(fs.getAttribute('data-option-handle') || '')
    );
    if (colorOnly && typeOrCombo) {
      return { typeFs: typeOrCombo, colorFs: colorOnly };
    }
    return { typeFs: null, colorFs: colorOnly || typeOrCombo || null };
  };

  const getFieldsetValue = (fieldset) => {
    if (!fieldset) return '';
    const hidden = fieldset.querySelector(
      'variant-dropdown-select input[type="hidden"]'
    );
    if (hidden && hidden.value) return hidden.value;
    const radio = fieldset.querySelector('input[type="radio"]:checked');
    return radio ? radio.value : '';
  };

  const activateRadio = (radio) => {
    if (!radio) return false;
    radio.classList.remove('disabled');
    if (radio.disabled) radio.disabled = false;
    if (radio.checked) {
      radio.checked = false;
      radio.removeAttribute('checked');
    }
    radio.click();
    if (!radio.checked) {
      const fieldset = radio.closest('fieldset');
      fieldset?.querySelectorAll('input[type="radio"]').forEach((r) => {
        r.checked = false;
        r.removeAttribute('checked');
      });
      radio.checked = true;
      radio.setAttribute('checked', '');
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return !!radio.checked;
  };

  const pickSiyahControl = (fieldset) => {
    const radios = [...fieldset.querySelectorAll('input[type="radio"]')];
    const siyahRadio =
      radios.find((r) => isSiyahValue(r.value)) ||
      radios.find((r) => !isFrameless(r.value));
    if (siyahRadio) return { kind: 'radio', el: siyahRadio, value: siyahRadio.value };
    const items = [
      ...fieldset.querySelectorAll('li[data-value], [data-value]'),
    ];
    const siyahItem =
      items.find((el) => isSiyahValue(el.dataset.value)) ||
      items.find((el) => !isFrameless(el.dataset.value));
    if (siyahItem) {
      return { kind: 'item', el: siyahItem, value: siyahItem.dataset.value };
    }
    return null;
  };

  const autoSelectDefaultFrame = (config) => {
    const gallery = galleryForConfig(config);
    const root =
      config.root ||
      (gallery && gallery.closest('quick-add-modal, .quick-add-modal, .product')) ||
      document.getElementById(`shopify-section-${config.sectionId}`) ||
      document;
    const { typeFs, colorFs } = findFrameFieldsets(root);
    if (!colorFs && !typeFs) return false;

    const picker =
      (colorFs && colorFs.closest('variant-radios, variant-selects')) ||
      (typeFs && typeFs.closest('variant-radios, variant-selects'));
    const targetFs = colorFs || typeFs;
    const alreadyApplied =
      picker?.dataset.pfpDefaultApplied === '1' || targetFs?.dataset.pfpDefaultApplied === '1';
    if (alreadyApplied) {
      const until = Number(picker?.dataset.pfpDefaultUntil || targetFs?.dataset.pfpDefaultUntil || 0);
      const current = getFieldsetValue(targetFs);
      if (!isFrameless(current) || Date.now() > until) return true;
    }

    if (typeFs) {
      const framed = [...typeFs.querySelectorAll('input[type="radio"]')].find(
        (r) => isFramedTypeValue(r.value)
      );
      if (framed && !framed.checked) activateRadio(framed);
    }

    const pick = pickSiyahControl(targetFs);
    if (!pick) return false;

    if (pick.kind === 'radio') activateRadio(pick.el);
    else pick.el.click();

    const chosen = getFieldsetValue(targetFs) || pick.value;
    if (!chosen || isFrameless(chosen)) return false;

    const until = String(Date.now() + 1200);
    if (picker) {
      picker.dataset.pfpDefaultApplied = '1';
      picker.dataset.pfpDefaultUntil = until;
    }
    targetFs.dataset.pfpDefaultApplied = '1';
    targetFs.dataset.pfpDefaultUntil = until;
    if (gallery?._pfp) gallery._pfp.syncFrameFromDom(chosen);
    return true;
  };

  const settleGallery = (config, applied) => {
    const gallery = galleryForConfig(config);
    if (!gallery) return;
    if (applied) gallery.dataset.pfpDefaultSettled = '1';
    if (gallery._pfp) gallery._pfp.scheduleUpdate();
    else if (applied) gallery.classList.add('pfp-ready');
  };

  const applyDefaultFrame = (config) => {
    const applied = autoSelectDefaultFrame(config);
    if (applied) settleGallery(config, true);
    return applied;
  };

  const scheduleDefaultFrame = (config) => {
    if (applyDefaultFrame(config)) return;
    [40, 120, 280, 520, 900, 1500, 2200].forEach((ms) => {
      setTimeout(() => applyDefaultFrame(config), ms);
    });
    setTimeout(() => settleGallery(config, true), 2500);
  };

  const boot = () => {
    document
      .querySelectorAll('script[type="application/json"][data-pfp-config]')
      .forEach((script) => {
        let config;
        try {
          config = JSON.parse(script.textContent);
        } catch (e) {
          console.warn('[frame-preview] config parse failed', e);
          return;
        }
        config.root = scriptRoot(script) || document;
        if (!script.dataset.pfpInstance) {
          script.dataset.pfpInstance = '1';
          new ProductFramePreview(config);
        }
        if (!script.dataset.pfpDefaultScheduled) {
          script.dataset.pfpDefaultScheduled = '1';
          scheduleDefaultFrame(config);
        } else {
          applyDefaultFrame(config);
        }
      });
  };

  window.AlaPfpBoot = boot;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
